'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@clerk/nextjs';
import { NavbarAccountControls } from '@/components/layout/navbar-account-controls';
import { OrgAccountSwitcher } from '@/components/layout/org-account-switcher';
import { UserAccountMenu } from '@/components/layout/user-account-menu';
import { ChevronDown, Menu, PanelLeftClose, Pin, PinOff, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { LogoIcon } from '@/components/shared/logo-icon';
import { GlobeIcon } from '@/components/shared/icons';
import { NavItemIcon } from '@/components/layout/nav-item-icon';
import { NavbarLocalhostBadge } from '@/components/layout/navbar-localhost-badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  isClerkPopoverOrPortal,
  isClerkPortalAtPoint,
  preventMobileSheetOutsideDismiss,
} from '@/components/layout/clerk-portal-guard';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppNavItems } from '@/hooks/use-app-nav-items';
import { useAppState } from '@/hooks/use-app-state';
import { Badge } from '@/components/ui/badge';
import { isNavActive, isNavDropdownActive } from '@/lib/navigation/is-nav-active';
import type { AppNavItem } from '@/lib/navigation/app-nav-types';
import {
  hasPaidSubscriptionClient,
  isSuperAdminViewClient,
} from '@/lib/permissions/client';
import { useRTLUtils, useLanguage } from '@/lib/translation';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { MobileAppSidePanel } from '@/components/layout/mobile-app-side-panel';
import { MobileAppSideSheetHeader } from '@/components/layout/mobile-app-side-sheet-header';
import { mobileShellTopBarMenuTriggerClassName } from '@/components/layout/mobile-shell-top-bar';
import { cn } from '@/lib/utils';

const SIDEBAR_COLLAPSED_KEY = 'reach-enterprise-sidebar-collapsed';
/** @deprecated Legacy boolean; migrated to {@link SIDEBAR_PIN_MODE_KEY}. */
const SIDEBAR_PINNED_KEY = 'reach-enterprise-sidebar-pinned';
const SIDEBAR_PIN_MODE_KEY = 'reach-enterprise-sidebar-pin-mode';

/** Desktop sidebar layout — cycled via the pin control. */
export type { EnterpriseSidebarPinMode } from '@/components/layout/enterprise-sidebar-dimensions';

const PIN_MODE_CYCLE: EnterpriseSidebarPinMode[] = [
  'overlay',
  'pinned-collapsed',
  'pinned-expanded',
];

function readStoredPinMode(): EnterpriseSidebarPinMode {
  try {
    const modeRaw = window.localStorage.getItem(SIDEBAR_PIN_MODE_KEY);
    if (
      modeRaw === 'overlay' ||
      modeRaw === 'pinned-collapsed' ||
      modeRaw === 'pinned-expanded'
    ) {
      return modeRaw;
    }
    if (window.localStorage.getItem(SIDEBAR_PINNED_KEY) === '1') {
      return 'pinned-expanded';
    }
  } catch {
    /* ignore */
  }
  return 'overlay';
}

function persistPinMode(next: EnterpriseSidebarPinMode) {
  window.localStorage.setItem(SIDEBAR_PIN_MODE_KEY, next);
  window.localStorage.setItem(SIDEBAR_PINNED_KEY, next === 'overlay' ? '0' : '1');
  window.localStorage.setItem(
    SIDEBAR_COLLAPSED_KEY,
    next === 'pinned-expanded' ? '0' : '1',
  );
}
/** Ignore brief pointer passes when moving between screens. */
const SIDEBAR_HOVER_EXPAND_DELAY_MS = 40;
const SIDEBAR_HOVER_COLLAPSE_DELAY_MS = 120;

import {
  ENTERPRISE_SIDEBAR_LABEL_MAX,
  ENTERPRISE_SIDEBAR_WIDTH_COLLAPSED,
  ENTERPRISE_SIDEBAR_WIDTH_EXPANDED,
  type EnterpriseSidebarPinMode,
} from '@/components/layout/enterprise-sidebar-dimensions';

const PANEL_SHELL =
  'relative overflow-hidden border-emerald-900/40 bg-gradient-to-b from-emerald-950 via-emerald-900 to-emerald-950 text-emerald-50';

const NAV_SCROLL = 'min-h-0 flex-1 overflow-x-hidden scrollbar-enterprise';

/** Fixed 40×40 slot — icons stay here when labels show/hide (collapsed vs hover-expand). */
const NAV_ICON_SLOT = 'flex size-10 shrink-0 items-center justify-center';

const NAV_ROW_BASE =
  'flex h-10 w-full min-w-0 items-center rounded-lg border text-sm font-medium transition-[background-color,border-color,box-shadow]';

/** 44px minimum touch target (iOS HIG) for mobile sheet navigation. */
const MOBILE_NAV_ROW = 'min-h-11 h-11 touch-manipulation';
const MOBILE_DROPDOWN_CHILD =
  'flex min-h-11 items-center rounded-md px-2.5 py-2.5 text-sm touch-manipulation';

function navRowClass(mobileComfortable?: boolean): string {
  return cn(NAV_ROW_BASE, mobileComfortable && MOBILE_NAV_ROW);
}

function sidebarRevealLabelClass(collapsed: boolean): string {
  return cn(
    'min-w-0 truncate transition-[max-width,opacity,padding] duration-150 ease-out',
    collapsed ? 'max-w-0 overflow-hidden px-0 opacity-0' : cn(ENTERPRISE_SIDEBAR_LABEL_MAX, 'flex-1 px-0.5 opacity-100'),
  );
}

function sidebarRevealTrailingClass(collapsed: boolean): string {
  return cn(
    'shrink-0 transition-[max-width,opacity,margin] duration-150 ease-out',
    collapsed ? 'ml-0 max-w-0 overflow-hidden opacity-0' : 'max-w-4 opacity-100',
  );
}

/** Collapses a 40×40 slot (e.g. Clerk avatar) when the sidebar is icon-only. */
function sidebarRevealIconSlotClass(collapsed: boolean): string {
  return cn(
    NAV_ICON_SLOT,
    'overflow-visible transition-[max-width,opacity,padding] duration-150 ease-out',
    collapsed ? 'max-w-0 overflow-hidden p-0 opacity-0' : 'max-w-10 opacity-100',
  );
}

type EnterpriseSidebarLayoutValue = {
  /** Visual collapse: false when pinned expanded or hover-expanded. */
  collapsed: boolean;
  pinMode: EnterpriseSidebarPinMode;
  /** True when {@link pinMode} is not `overlay` (collapsed or expanded pin). */
  isPinned: boolean;
  isHoverExpanded: boolean;
  togglePinned: () => void;
  onSidebarPointerEnter: () => void;
  onSidebarPointerLeave: (event: ReactMouseEvent<HTMLElement>) => void;
  enableHoverExpand: boolean;
  /** Keep hover-expand while a portaled account/org dropdown is open. */
  registerAccountMenuOpen: (open: boolean) => void;
};

const EnterpriseSidebarLayoutContext = createContext<EnterpriseSidebarLayoutValue | null>(null);

export function EnterpriseSidebarLayoutProvider({ children }: { children: React.ReactNode }) {
  const value = useEnterpriseSidebarLayoutState();
  return (
    <EnterpriseSidebarLayoutContext.Provider value={value}>
      {children}
    </EnterpriseSidebarLayoutContext.Provider>
  );
}

export function useEnterpriseSidebarLayout(): EnterpriseSidebarLayoutValue {
  const ctx = useContext(EnterpriseSidebarLayoutContext);
  if (ctx) return ctx;
  return useEnterpriseSidebarLayoutState();
}

function useEnterpriseSidebarLayoutState(): EnterpriseSidebarLayoutValue {
  const [storedCollapsed, setStoredCollapsed] = useState(true);
  const [pinMode, setPinMode] = useState<EnterpriseSidebarPinMode>('overlay');
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [canHoverExpand, setCanHoverExpand] = useState(false);
  const accountMenuOpenCountRef = useRef(0);

  useEffect(() => {
    try {
      const collapsedRaw = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      setStoredCollapsed(collapsedRaw === null ? true : collapsedRaw === '1');
      setPinMode(readStoredPinMode());
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCanHoverExpand(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const togglePinned = useCallback(() => {
    setPinMode((prev) => {
      const idx = PIN_MODE_CYCLE.indexOf(prev);
      const next = PIN_MODE_CYCLE[(idx + 1) % PIN_MODE_CYCLE.length];
      try {
        persistPinMode(next);
        setStoredCollapsed(next !== 'pinned-expanded');
        setIsHoverExpanded(false);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const isPinned = pinMode !== 'overlay';
  const enableHoverExpand = pinMode === 'overlay' && storedCollapsed && canHoverExpand;
  const hoverExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPointerRef = useRef({ x: 0, y: 0 });

  const clearHoverExpandTimer = useCallback(() => {
    if (hoverExpandTimerRef.current !== null) {
      clearTimeout(hoverExpandTimerRef.current);
      hoverExpandTimerRef.current = null;
    }
  }, []);

  const clearHoverCollapseTimer = useCallback(() => {
    if (hoverCollapseTimerRef.current !== null) {
      clearTimeout(hoverCollapseTimerRef.current);
      hoverCollapseTimerRef.current = null;
    }
  }, []);

  const clearHoverTimers = useCallback(() => {
    clearHoverExpandTimer();
    clearHoverCollapseTimer();
  }, [clearHoverExpandTimer, clearHoverCollapseTimer]);

  useEffect(() => {
    if (!enableHoverExpand) clearHoverTimers();
  }, [enableHoverExpand, clearHoverTimers]);

  useEffect(() => {
    if (!enableHoverExpand) return;
    const onPointerMove = (event: PointerEvent) => {
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
    };
    document.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => document.removeEventListener('pointermove', onPointerMove);
  }, [enableHoverExpand]);

  /** Collapse when the pointer leaves both the sidebar and any portaled Clerk menu. */
  useEffect(() => {
    if (!enableHoverExpand || !isHoverExpanded) return;

    let awayTimer: ReturnType<typeof setTimeout> | null = null;

    const onPointerMove = (event: PointerEvent) => {
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      const target = event.target as HTMLElement;
      const overSidebar = Boolean(target.closest('[data-enterprise-sidebar="true"]'));
      const overClerk = isClerkPopoverOrPortal(target);

      if (overSidebar || overClerk) {
        if (awayTimer !== null) {
          clearTimeout(awayTimer);
          awayTimer = null;
        }
        return;
      }

      if (awayTimer !== null) clearTimeout(awayTimer);
      awayTimer = setTimeout(() => {
        awayTimer = null;
        if (accountMenuOpenCountRef.current > 0) return;
        const { x, y } = lastPointerRef.current;
        if (isClerkPortalAtPoint(x, y)) return;
        setIsHoverExpanded(false);
      }, SIDEBAR_HOVER_COLLAPSE_DELAY_MS);
    };

    document.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      if (awayTimer !== null) clearTimeout(awayTimer);
    };
  }, [enableHoverExpand, isHoverExpanded]);

  useEffect(() => () => clearHoverTimers(), [clearHoverTimers]);

  const registerAccountMenuOpen = useCallback(
    (open: boolean) => {
      accountMenuOpenCountRef.current = Math.max(
        0,
        accountMenuOpenCountRef.current + (open ? 1 : -1),
      );
      if (open && enableHoverExpand) {
        setIsHoverExpanded(true);
      }
    },
    [enableHoverExpand],
  );

  const onSidebarPointerEnter = useCallback(() => {
    clearHoverCollapseTimer();
    if (!enableHoverExpand) return;

    clearHoverExpandTimer();
    hoverExpandTimerRef.current = setTimeout(() => {
      hoverExpandTimerRef.current = null;
      setIsHoverExpanded(true);
    }, SIDEBAR_HOVER_EXPAND_DELAY_MS);
  }, [enableHoverExpand, clearHoverCollapseTimer, clearHoverExpandTimer]);

  const onSidebarPointerLeave = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (!enableHoverExpand) return;

      clearHoverExpandTimer();

      const related = event.relatedTarget;
      if (related instanceof HTMLElement && isClerkPopoverOrPortal(related)) {
        return;
      }

      if (accountMenuOpenCountRef.current > 0) return;

      clearHoverCollapseTimer();
      hoverCollapseTimerRef.current = setTimeout(() => {
        hoverCollapseTimerRef.current = null;
        if (accountMenuOpenCountRef.current > 0) return;
        const { x, y } = lastPointerRef.current;
        if (isClerkPortalAtPoint(x, y)) return;
        setIsHoverExpanded(false);
      }, SIDEBAR_HOVER_COLLAPSE_DELAY_MS);
    },
    [enableHoverExpand, clearHoverCollapseTimer, clearHoverExpandTimer],
  );

  const collapsed =
    pinMode === 'pinned-expanded'
      ? false
      : pinMode === 'pinned-collapsed'
        ? true
        : storedCollapsed && !isHoverExpanded;

  return {
    collapsed,
    pinMode,
    isPinned,
    isHoverExpanded,
    togglePinned,
    onSidebarPointerEnter,
    onSidebarPointerLeave,
    enableHoverExpand,
    registerAccountMenuOpen,
  };
}

function navItemTone(
  isBilling: boolean,
  isSubscriptionView: boolean,
  hasPaid: boolean,
  usageTabColor: 'red' | 'yellow' | null,
  active: boolean,
  isSuperAdminTab = false,
): string {
  if (isSuperAdminTab) {
    return previewAdminNavRowTone(active);
  }
  if (!isBilling || !isSubscriptionView || hasPaid) {
    return previewNavRowTone(active);
  }
  if (usageTabColor === 'red') {
    return active
      ? 'border-red-400/50 bg-red-500/25 text-red-50'
      : 'border-transparent text-red-200 hover:bg-red-950/40';
  }
  if (usageTabColor === 'yellow') {
    return active
      ? 'border-amber-400/50 bg-amber-500/20 text-amber-50'
      : 'border-transparent text-amber-100 hover:bg-amber-950/35';
  }
  return previewNavRowTone(active);
}

function previewNavRowTone(active: boolean): string {
  return active
    ? 'border-emerald-400/50 bg-emerald-500/25 text-white shadow-sm shadow-emerald-950/40'
    : 'border-transparent text-emerald-50/95 hover:bg-emerald-800/50 hover:text-white';
}

function previewAdminNavRowTone(active: boolean): string {
  return active
    ? 'border-amber-400/45 bg-amber-500/20 text-amber-50 shadow-sm shadow-emerald-950/40'
    : 'border-transparent text-amber-100/90 hover:bg-amber-950/40 hover:text-amber-50';
}

type PreviewNavLink = {
  href: string;
  label: string;
  icon: string;
  isActive: (pathname: string) => boolean;
};

/** Store / Partners dev preview + admin shortcuts for localhost or super-admin in enterprise shell. */
function SidebarSubscriptionPreviewSection({
  collapsed,
  mobileComfortable,
  onNavigate,
}: {
  collapsed: boolean;
  mobileComfortable?: boolean;
  onNavigate?: () => void;
}) {
  const pathname = usePathname() ?? '';
  const { t } = useTranslation();
  const { directionClass, textAlignClass, isRTL } = useRTLUtils();
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const { orgRole, sessionClaims } = useAuth();
  const isSuperAdmin = isSuperAdminViewClient(orgRole, sessionClaims);
  const hasPaid = hasPaidSubscriptionClient(sessionClaims);

  /** Store / partners preview + admin shortcuts — Kindway super-admins only (desktop sidebar). */
  if (!isSuperAdmin) {
    return null;
  }

  const partnersHref = hasPaid ? '/partners' : '/partners?preview=1';

  const saasPreviewLinks: PreviewNavLink[] = [
    {
      href: '/reachstore',
      label: t('navigation.store'),
      icon: 'store',
      isActive: (p) => isNavActive(p, '/reachstore'),
    },
    {
      href: partnersHref,
      label: t('navigation.partners'),
      icon: 'partners',
      isActive: (p) => p === '/partners' || p.startsWith('/partners/'),
    },
  ];

  const adminLinks: PreviewNavLink[] = isSuperAdmin
    ? [
        {
          href: '/admin',
          label: 'Admin',
          icon: 'admin',
          isActive: (p) => p === '/admin',
        },
        {
          href: '/admin/outreach',
          label: 'Outreach',
          icon: 'outreach',
          isActive: (p) => p === '/admin/outreach' || p.startsWith('/admin/outreach/'),
        },
        {
          href: '/admin/org-management',
          label: 'Organizations',
          icon: 'orgManagement',
          isActive: (p) => p === '/admin/org-management' || p.startsWith('/admin/org-management/'),
        },
      ]
    : [];

  const renderLink = (
    item: PreviewNavLink,
    variant: 'customer' | 'admin' = 'customer',
  ) => {
    const active = item.isActive(pathname);
    const rowTone = variant === 'admin' ? previewAdminNavRowTone(active) : previewNavRowTone(active);
    const iconClass =
      variant === 'admin'
        ? active
          ? 'text-amber-50'
          : 'text-amber-200/90'
        : active
          ? 'text-white'
          : 'text-emerald-100';

    const link = (
      <Link
        href={item.href}
        onClick={onNavigate}
        className={cn(navRowClass(mobileComfortable), rowTone)}
      >
        <span className={NAV_ICON_SLOT}>
          <NavItemIcon icon={item.icon} className={iconClass} />
        </span>
        <span className={cn(sidebarRevealLabelClass(collapsed), textAlignClass)}>{item.label}</span>
      </Link>
    );

    return (
      <NavItemTooltip key={item.href} label={item.label} collapsed={collapsed}>
        {link}
      </NavItemTooltip>
    );
  };

  const languageLabel = t('language.switchLanguage', 'Switch Language');
  const [languageOpen, setLanguageOpen] = useState(false);

  const selectLanguage = (code: string) => {
    changeLanguage(code);
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const languageRowButtonClass = cn(navRowClass(mobileComfortable), previewNavRowTone(false));

  const languageControl = mobileComfortable ? (
    <div className="flex w-full flex-col gap-0.5">
      <button
        type="button"
        onClick={() => setLanguageOpen((open) => !open)}
        className={languageRowButtonClass}
        aria-label={languageLabel}
        aria-expanded={languageOpen}
      >
        <span className={NAV_ICON_SLOT}>
          <GlobeIcon size={16} />
        </span>
        <span className={cn(sidebarRevealLabelClass(collapsed), textAlignClass)}>
          {currentLanguage.name}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 opacity-70 transition-transform',
            sidebarRevealTrailingClass(collapsed),
            languageOpen && 'rotate-180',
          )}
        />
      </button>
      {languageOpen ? (
        <div
          className={cn(
            'mt-0.5 flex flex-col gap-0.5 pb-1 ps-10',
            isRTL ? 'border-r border-emerald-700/60 pe-2' : 'border-l border-emerald-700/60',
          )}
        >
          {supportedLanguages.map((language) => {
            const selected = currentLanguage.code === language.code;
            return (
              <button
                key={language.code}
                type="button"
                onClick={() => selectLanguage(language.code)}
                className={cn(
                  MOBILE_DROPDOWN_CHILD,
                  'w-full rounded-md text-emerald-100 transition-colors hover:bg-emerald-800/50',
                  selected && 'bg-emerald-800/60 font-medium',
                  textAlignClass,
                  directionClass,
                )}
              >
                <span className="min-w-0 flex-1 truncate">{language.name}</span>
                {selected ? (
                  <div className={cn('ms-auto h-2 w-2 shrink-0 rounded-full bg-emerald-400', isRTL && 'ms-0 me-auto')} />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  ) : (
    <DropdownMenu modal={false}>
      <NavItemTooltip label={languageLabel} collapsed={collapsed}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={languageRowButtonClass}
            aria-label={languageLabel}
          >
            <span className={NAV_ICON_SLOT}>
              <GlobeIcon size={16} />
            </span>
            <span className={cn(sidebarRevealLabelClass(collapsed), textAlignClass)}>
              {currentLanguage.name}
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 opacity-70',
                sidebarRevealTrailingClass(collapsed),
              )}
            />
          </button>
        </DropdownMenuTrigger>
      </NavItemTooltip>
      <DropdownMenuContent
        side={isRTL ? 'left' : 'right'}
        align="start"
        className="min-w-[10rem] border-emerald-800 bg-emerald-950 text-emerald-50"
      >
        {supportedLanguages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => selectLanguage(language.code)}
            className={cn('cursor-pointer', directionClass)}
          >
            <span className={textAlignClass}>{language.name}</span>
            {currentLanguage.code === language.code ? (
              <div
                className={cn(
                  'ms-auto h-2 w-2 rounded-full bg-emerald-400',
                  isRTL && 'ms-0 me-auto',
                )}
              />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className={cn('flex w-full flex-col gap-0.5', directionClass)}>
      <div className="my-1 border-t border-emerald-800/60" aria-hidden />

      <div className="flex w-full flex-col gap-0.5">
        {/* Amber tone — SaaS-only preview; not part of normal custom-org nav */}
        {saasPreviewLinks.map((item) => renderLink(item, 'admin'))}
      </div>

      {adminLinks.length > 0 ? (
        <>
          <div className="my-1 border-t border-emerald-800/60" aria-hidden />
          <div className="flex w-full flex-col gap-0.5">
            {adminLinks.map((item) => renderLink(item, 'admin'))}
          </div>
        </>
      ) : null}

      <div className="my-1 border-t border-emerald-800/60" aria-hidden />
      {languageControl}
    </div>
  );
}

function SidebarOrgFooter({
  collapsed,
  mobileComfortable,
  onPortaledMenuOpenChange,
}: {
  collapsed: boolean;
  mobileComfortable?: boolean;
  onPortaledMenuOpenChange?: (open: boolean) => void;
}) {
  if (mobileComfortable) return null;

  return (
    <div
      className={cn(
        'shrink-0 border-t border-emerald-800/70 pt-2',
        collapsed ? 'px-1 pb-2' : 'px-0.5 pb-2',
      )}
    >
      <OrgAccountSwitcher
        variant="enterprise-sidebar"
        collapsed={collapsed}
        mobileComfortable={mobileComfortable}
        onPortaledMenuOpenChange={onPortaledMenuOpenChange}
      />
    </div>
  );
}

/** Reach home in the icon column; account menu reveals on the outer edge when expanded. */
function SidebarHeaderRow({
  collapsed,
  mobileComfortable,
  onPortaledMenuOpenChange,
}: {
  collapsed: boolean;
  mobileComfortable?: boolean;
  onPortaledMenuOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { directionClass } = useRTLUtils();
  const rowHeight = mobileComfortable ? 'min-h-11 h-11' : 'h-10';
  const showProfile = !mobileComfortable && !collapsed;

  return (
    <div className={cn('flex w-full min-w-0 items-center', rowHeight, directionClass)}>
      <Link
        href="/dashboard"
        className={cn(
          'flex min-w-0 flex-1 items-center rounded-lg border border-transparent transition-[background-color] hover:bg-emerald-800/40',
          rowHeight,
          mobileComfortable && 'touch-manipulation',
        )}
        aria-label="Reach"
      >
        <span className={NAV_ICON_SLOT}>
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-800/80 ring-1 ring-emerald-600/40">
            <LogoIcon className="h-6 w-6" />
          </div>
        </span>
        <span
          className={cn(
            mobileComfortable
              ? 'min-w-0 flex-1 truncate px-0.5 text-sm font-medium text-emerald-50'
              : sidebarRevealLabelClass(collapsed),
            !mobileComfortable && 'flex items-baseline gap-1.5',
          )}
        >
          {mobileComfortable ? (
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="shrink-0 text-sm font-bold leading-none text-white">Reach</span>
              <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-300/90">
                {t('navigation.customEnterprise', 'Enterprise')}
              </span>
            </span>
          ) : (
            <>
              <span className="shrink-0 text-sm font-bold leading-none text-white">Reach</span>
              <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-300/90">
                {t('navigation.customEnterprise', 'Enterprise')}
              </span>
            </>
          )}
        </span>
      </Link>
      <span className={sidebarRevealIconSlotClass(collapsed)} aria-hidden={!showProfile}>
        {showProfile ? (
          <UserAccountMenu
            variant="enterprise-sidebar"
            onPortaledMenuOpenChange={onPortaledMenuOpenChange}
          />
        ) : null}
      </span>
    </div>
  );
}

function sidebarPinAriaLabel(t: TFunction, pinMode: EnterpriseSidebarPinMode): string {
  switch (pinMode) {
    case 'pinned-expanded':
      return t('navigation.sidebarPinModeExpanded', 'Sidebar pinned open');
    case 'pinned-collapsed':
      return t('navigation.sidebarPinModeCollapsed', 'Sidebar pinned collapsed');
    default:
      return t('navigation.sidebarPinModeOverlay', 'Sidebar overlays content');
  }
}

function SidebarPinIcon({ pinMode }: { pinMode: EnterpriseSidebarPinMode }) {
  switch (pinMode) {
    case 'pinned-expanded':
      return <PinOff className="h-4 w-4" aria-hidden />;
    case 'pinned-collapsed':
      return <PanelLeftClose className="h-4 w-4" aria-hidden />;
    default:
      return <Pin className="h-4 w-4" aria-hidden />;
  }
}

function SidebarPinButton({
  collapsed,
  pinMode,
  onTogglePinned,
}: {
  collapsed: boolean;
  pinMode: EnterpriseSidebarPinMode;
  onTogglePinned: () => void;
}) {
  const { t } = useTranslation();
  const ariaLabel = sidebarPinAriaLabel(t, pinMode);

  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        'h-10 w-10 shrink-0 text-emerald-200/80 hover:bg-emerald-800/60 hover:text-white',
        pinMode !== 'overlay' && 'bg-emerald-800/50 text-emerald-50',
      )}
      onClick={onTogglePinned}
      aria-label={ariaLabel}
      aria-pressed={pinMode !== 'overlay'}
    >
      <SidebarPinIcon pinMode={pinMode} />
    </Button>
  );

  if (collapsed) {
    return (
      <NavItemTooltip label={ariaLabel} collapsed>
        {button}
      </NavItemTooltip>
    );
  }

  return button;
}

/** Localhost mark + pin — pin on the outer panel edge; Local is standalone (no borders). */
function SidebarFooterMarkingRow({
  collapsed,
  pinMode,
  onTogglePinned,
  showLocalBadge,
  showPinControl,
  mobileComfortable,
}: {
  collapsed: boolean;
  pinMode: EnterpriseSidebarPinMode;
  onTogglePinned: () => void;
  showLocalBadge?: boolean;
  showPinControl?: boolean;
  mobileComfortable?: boolean;
}) {
  const { directionClass } = useRTLUtils();
  const showLocalInFooter = showLocalBadge && (!collapsed || mobileComfortable);

  if (!showLocalInFooter && !showPinControl) {
    return null;
  }

  const stackControls =
    collapsed && !mobileComfortable && !showLocalInFooter && showPinControl;

  const trailingControls = showPinControl ? (
    <span className={NAV_ICON_SLOT}>
      <SidebarPinButton
        collapsed={collapsed}
        pinMode={pinMode}
        onTogglePinned={onTogglePinned}
      />
    </span>
  ) : null;

  return (
    <div
      className={cn(
        'flex w-full shrink-0 gap-2 px-1',
        stackControls ? 'flex-col items-center py-1' : 'flex-row items-center',
        showLocalInFooter ? 'min-h-10 py-1' : 'py-2',
        !showLocalInFooter && !stackControls && 'justify-end',
        showLocalInFooter && !stackControls && 'justify-between',
        directionClass,
      )}
    >
      {showLocalInFooter ? (
        <div className="flex shrink-0 items-center ps-1">
          <NavbarLocalhostBadge className="text-[2rem] leading-none sm:text-[2.125rem]" />
        </div>
      ) : null}
      <div className={cn('flex shrink-0 items-center gap-1', stackControls && 'flex-col gap-1')}>
        {trailingControls}
      </div>
    </div>
  );
}

function NavItemTooltip({
  label,
  collapsed,
  children,
}: {
  label: string;
  collapsed: boolean;
  children: ReactNode;
}) {
  const { isRTL } = useRTLUtils();
  if (!collapsed) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={isRTL ? 'left' : 'right'} className="text-sm">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarNav({
  navItems,
  onNavigate,
  isSubscriptionView,
  hasPaid,
  usageTabColor,
  collapsed,
  pinMode,
  mobileComfortable,
}: {
  navItems: AppNavItem[];
  onNavigate?: () => void;
  isSubscriptionView: boolean;
  hasPaid: boolean;
  usageTabColor: 'red' | 'yellow' | null;
  collapsed: boolean;
  pinMode: EnterpriseSidebarPinMode;
  mobileComfortable?: boolean;
}) {
  const pathname = usePathname() ?? '';
  const { directionClass, textAlignClass, isRTL } = useRTLUtils();
  const { data: appState } = useAppState();
  const pendingFeedbackApprovals = appState?.pendingFeedbackApprovals ?? 0;
  const showVotesPendingBadge = pendingFeedbackApprovals > 0;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const wasPinModeRef = useRef(pinMode);

  /** Pinned / mobile: expand dropdowns for the active route. Overlay: manual open only. */
  const autoOpenDropdownFromRoute = pinMode !== 'overlay' || !!mobileComfortable;

  useEffect(() => {
    if (!autoOpenDropdownFromRoute && collapsed) {
      setOpenGroups({});
    }
  }, [collapsed, autoOpenDropdownFromRoute]);

  /** Pinning expanded: keep open dropdowns and reveal sub-pages for the current route. */
  useEffect(() => {
    const justPinnedExpanded =
      pinMode === 'pinned-expanded' && wasPinModeRef.current !== 'pinned-expanded';
    wasPinModeRef.current = pinMode;
    if (!justPinnedExpanded) return;

    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of navItems) {
        if (!item.isDropdown || !item.dropdownItems) continue;
        const wasOpen = prev[item.label] === true;
        const routeOpen = isNavDropdownActive(pathname, item.dropdownItems);
        if (wasOpen || routeOpen) {
          if (!next[item.label]) {
            next[item.label] = true;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [pinMode, pathname, navItems]);

  const displayItems = [...navItems].reverse();

  const dropdownTone = (groupActive: boolean) =>
    groupActive
      ? 'border-emerald-400/50 bg-emerald-500/25 text-white'
      : 'border-transparent text-emerald-50/95 hover:bg-emerald-800/50 hover:text-white';

  return (
    <TooltipProvider delayDuration={0}>
      <nav className={cn('flex w-full flex-col gap-0.5', directionClass)} aria-label="Main">
        {displayItems.map((item) => {
          if (item.isDropdown && item.dropdownItems) {
            const groupHasActiveChild = isNavDropdownActive(pathname, item.dropdownItems);
            const parentTriggerActive =
              item.href !== '#' && item.href !== '' && isNavActive(pathname, item.href);
            const isOpen =
              openGroups[item.label] ?? (autoOpenDropdownFromRoute ? groupHasActiveChild : false);

            const iconRow = (
              <>
                <span className={NAV_ICON_SLOT}>
                  <NavItemIcon icon={item.icon} className="text-emerald-100" />
                </span>
                <span className={cn(sidebarRevealLabelClass(collapsed), textAlignClass)}>
                  {item.label}
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 opacity-70 transition-transform',
                    sidebarRevealTrailingClass(collapsed),
                    isOpen && !collapsed && 'rotate-180',
                  )}
                />
              </>
            );

            if (collapsed) {
              return (
                <DropdownMenu key={item.label} modal={false}>
                  <NavItemTooltip label={item.label} collapsed>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={cn(navRowClass(mobileComfortable), dropdownTone(parentTriggerActive))}
                        aria-label={item.label}
                      >
                        {iconRow}
                      </button>
                    </DropdownMenuTrigger>
                  </NavItemTooltip>
                  <DropdownMenuContent
                    side={isRTL ? 'left' : 'right'}
                    align="start"
                    className="min-w-[10rem] border-emerald-800 bg-emerald-950 text-emerald-50"
                  >
                    {item.dropdownItems.map((child) => {
                      const isVotesChild = child.href === '/votes' || child.href.startsWith('/votes?');
                      return (
                      <DropdownMenuItem key={child.href} asChild>
                        <Link
                          href={child.href}
                          onClick={onNavigate}
                          className={cn(
                            'cursor-pointer',
                            isNavActive(pathname, child.href) && 'bg-emerald-800/60 font-medium',
                          )}
                        >
                          <span className="inline-flex w-full items-center justify-between gap-3">
                            {child.label}
                            {isVotesChild && showVotesPendingBadge ? (
                              <Badge
                                variant="outline"
                                className="h-5 min-w-5 shrink-0 rounded-md border-orange-200 bg-orange-100 px-1.5 py-0 text-[10px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
                              >
                                {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
                              </Badge>
                            ) : null}
                          </span>
                        </Link>
                      </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }

            return (
              <div key={item.label} className="flex w-full flex-col">
                <button
                  type="button"
                  onClick={() =>
                    setOpenGroups((prev) => ({
                      ...prev,
                      [item.label]: !(prev[item.label] ?? (autoOpenDropdownFromRoute && groupHasActiveChild)),
                    }))
                  }
                  className={cn(navRowClass(mobileComfortable), dropdownTone(parentTriggerActive))}
                  aria-expanded={isOpen}
                >
                  {iconRow}
                </button>
                {isOpen ? (
                  <div
                    className={cn(
                      'mt-0.5 flex flex-col gap-0.5 pb-1 ps-10',
                      isRTL ? 'border-r border-emerald-700/60 pe-2' : 'border-l border-emerald-700/60',
                    )}
                  >
                    {item.dropdownItems.map((child) => {
                      const childActive = isNavActive(pathname, child.href);
                      const isVotesChild = child.href === '/votes' || child.href.startsWith('/votes?');
                      return (
                        <Link
                          key={child.href}
                          href={child.href}
                          onClick={onNavigate}
                          className={cn(
                            mobileComfortable ? MOBILE_DROPDOWN_CHILD : 'rounded-md px-2.5 py-1.5 text-sm',
                            'flex items-center justify-between gap-2 transition-colors',
                            textAlignClass,
                            childActive
                              ? 'bg-emerald-500/20 font-medium text-white'
                              : 'text-emerald-100/90 hover:bg-emerald-800/40 hover:text-white',
                          )}
                        >
                          <span className="min-w-0 truncate">{child.label}</span>
                          {isVotesChild && showVotesPendingBadge ? (
                            <Badge
                              variant="outline"
                              className="h-5 min-w-5 shrink-0 rounded-md border-orange-200 bg-orange-100 px-1.5 py-0 text-[10px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
                            >
                              {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
                            </Badge>
                          ) : null}
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          }

          const active = isNavActive(pathname, item.href);
          const isBilling = item.href === '/billing';
          const isVotesItem = item.href === '/votes' || item.href.startsWith('/votes?');
          const rowTone = navItemTone(
            isBilling,
            isSubscriptionView,
            hasPaid,
            usageTabColor,
            active,
            Boolean(item.superAdminOnly),
          );

          const link = (
            <Link href={item.href} onClick={onNavigate} className={cn(navRowClass(mobileComfortable), rowTone)}>
              <span
                className={cn(
                  NAV_ICON_SLOT,
                  isVotesItem && showVotesPendingBadge && collapsed && 'relative',
                )}
              >
                <NavItemIcon
                  icon={item.icon}
                  className={
                    item.superAdminOnly
                      ? active
                        ? 'text-amber-50'
                        : 'text-amber-200/90'
                      : active
                        ? 'text-white'
                        : 'text-emerald-100'
                  }
                />
                {isVotesItem && showVotesPendingBadge && collapsed ? (
                  <Badge
                    variant="outline"
                    className="absolute -top-0.5 -end-0.5 h-4 min-w-4 rounded-md border-orange-200 bg-orange-100 px-1 py-0 text-[9px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
                  >
                    {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
                  </Badge>
                ) : null}
              </span>
              <span
                className={cn(
                  sidebarRevealLabelClass(collapsed),
                  textAlignClass,
                  !collapsed && isVotesItem && showVotesPendingBadge && 'flex flex-1 items-center gap-2',
                )}
              >
                {item.label}
                {isVotesItem && showVotesPendingBadge && !collapsed ? (
                  <Badge
                    variant="outline"
                    className="ms-auto h-5 min-w-5 shrink-0 rounded-md border-orange-200 bg-orange-100 px-1.5 py-0 text-[10px] font-semibold leading-none text-orange-800 tabular-nums dark:border-orange-700/80 dark:bg-orange-950/40 dark:text-orange-200"
                  >
                    {pendingFeedbackApprovals > 99 ? '99+' : pendingFeedbackApprovals}
                  </Badge>
                ) : null}
              </span>
            </Link>
          );

          return (
            <NavItemTooltip key={item.href} label={item.label} collapsed={collapsed}>
              {link}
            </NavItemTooltip>
          );
        })}
      </nav>
    </TooltipProvider>
  );
}

function EnterpriseSidebarPanel({
  collapsed,
  pinMode,
  onTogglePinned,
  onNavigate,
  showLocalBadge,
  showPinControl = true,
  mobileComfortable,
  onPortaledMenuOpenChange,
}: {
  collapsed: boolean;
  pinMode: EnterpriseSidebarPinMode;
  onTogglePinned: () => void;
  onNavigate?: () => void;
  showLocalBadge?: boolean;
  showPinControl?: boolean;
  mobileComfortable?: boolean;
  onPortaledMenuOpenChange?: (open: boolean) => void;
}) {
  const { navItems, isSubscriptionView, hasPaid, usageTabColor } = useAppNavItems();

  return (
    <TooltipProvider delayDuration={0}>
      <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
        <div className="relative z-10 flex h-full min-h-0 flex-col px-2 py-3">
        <div className="flex shrink-0 flex-col gap-3">
          <SidebarHeaderRow
            collapsed={collapsed}
            mobileComfortable={mobileComfortable}
            onPortaledMenuOpenChange={onPortaledMenuOpenChange}
          />
          {mobileComfortable ? (
            <NavbarAccountControls variant="sheet" theme="enterprise" />
          ) : null}
        </div>

        <div
          className={cn(
            NAV_SCROLL,
            'my-2 flex flex-col gap-2',
            mobileComfortable && 'overscroll-y-contain [-webkit-overflow-scrolling:touch]',
          )}
        >
          {mobileComfortable ? (
            <SidebarSubscriptionPreviewSection
              collapsed={collapsed}
              mobileComfortable={mobileComfortable}
              onNavigate={onNavigate}
            />
          ) : null}
          <SidebarNav
            navItems={navItems}
            onNavigate={onNavigate}
            isSubscriptionView={isSubscriptionView}
            hasPaid={hasPaid}
            usageTabColor={usageTabColor}
            collapsed={collapsed}
            pinMode={pinMode}
            mobileComfortable={mobileComfortable}
          />
          {!mobileComfortable ? (
            <SidebarSubscriptionPreviewSection
              collapsed={collapsed}
              mobileComfortable={mobileComfortable}
              onNavigate={onNavigate}
            />
          ) : null}
        </div>

        <div className="mt-auto flex shrink-0 flex-col">
          <SidebarFooterMarkingRow
            collapsed={collapsed}
            pinMode={pinMode}
            onTogglePinned={onTogglePinned}
            showLocalBadge={showLocalBadge}
            showPinControl={showPinControl}
            mobileComfortable={mobileComfortable}
          />
          <SidebarOrgFooter
            collapsed={collapsed}
            mobileComfortable={mobileComfortable}
            onPortaledMenuOpenChange={onPortaledMenuOpenChange}
          />
        </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

/** Desktop enterprise sidebar — same routes as SaaS top nav, different chrome. */
export function CustomAppSidebarDesktop() {
  const {
    collapsed,
    pinMode,
    togglePinned,
    onSidebarPointerEnter,
    onSidebarPointerLeave,
    enableHoverExpand,
    isHoverExpanded,
    registerAccountMenuOpen,
  } = useEnterpriseSidebarLayout();
  const { directionClass } = useRTLUtils();

  const isOverlayMode = pinMode === 'overlay';
  const overlayExpanded = isOverlayMode && isHoverExpanded && enableHoverExpand;

  const panel = (
    <EnterpriseSidebarPanel
      collapsed={collapsed}
      pinMode={pinMode}
      onTogglePinned={togglePinned}
      showLocalBadge
      onPortaledMenuOpenChange={registerAccountMenuOpen}
    />
  );

  /* Always fixed — never in flex flow.
     Pinned "push" effect is achieved via fixed start/end on the content panel (see CustomAppChromeBody),
     so expanding in unpinned/hover mode is a pure overlay that never shifts the page. */
  return (
    <aside
      data-enterprise-sidebar="true"
      className={cn(
        'print:hidden fixed inset-y-0 start-0 z-40 hidden h-dvh min-h-0 max-md:pointer-events-none max-md:invisible flex-col md:flex',
        'transition-[width] duration-150 ease-out',
        collapsed ? ENTERPRISE_SIDEBAR_WIDTH_COLLAPSED : ENTERPRISE_SIDEBAR_WIDTH_EXPANDED,
        pinMode === 'overlay' && isHoverExpanded && 'shadow-2xl shadow-emerald-950/50',
        PANEL_SHELL,
        directionClass === 'rtl' ? 'border-l' : 'border-r',
      )}
      onMouseEnter={onSidebarPointerEnter}
      onMouseLeave={onSidebarPointerLeave}
    >
      {panel}
    </aside>
  );
}

/** Mobile menu trigger + sheet — shared structure with SaaS; enterprise theme only. */
export function CustomAppMobileNav() {
  const { t } = useTranslation();
  const { directionClass, isRTL } = useRTLUtils();
  const [open, setOpen] = useState(false);
  const sheetSide = isRTL ? 'right' : 'left';

  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        data-mobile-nav-trigger
        className={mobileShellTopBarMenuTriggerClassName}
        aria-label={t('navigation.openMenu')}
        aria-expanded={open}
        aria-controls="enterprise-mobile-nav-sheet"
        onClick={() => setOpen(true)}
      >
        <Menu className="shrink-0" />
      </Button>
      <SheetContent
        id="enterprise-mobile-nav-sheet"
        side={sheetSide}
        dir="ltr"
        className={cn(
          'flex h-full max-h-dvh w-[12.5rem] max-w-[min(58vw,15rem)] shrink-0 flex-col gap-0 overflow-x-hidden p-0 sm:max-w-[17rem]',
          'border-emerald-900/40 bg-gradient-to-b from-emerald-950 via-emerald-900 to-emerald-950 text-emerald-50',
          'pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]',
        )}
        onInteractOutside={preventMobileSheetOutsideDismiss}
        onPointerDownOutside={preventMobileSheetOutsideDismiss}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div
          className={cn(
            'relative flex min-h-0 flex-1 flex-col overflow-hidden',
            directionClass,
          )}
        >
          <MobileAppSideSheetHeader theme="enterprise" onClose={() => setOpen(false)} />
          <div className="min-h-0 flex-1 overflow-hidden">
            <MobileAppSidePanel
              theme="enterprise"
              onNavigate={() => setOpen(false)}
              showLocalBadge
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
